export const DEFAULT_SUB_CATALOG_VALUE = 'default';

interface CatalogOptionSource {
  id: string;
  name?: string | null;
}

interface SelectedCatalogOption {
  value: string;
  label: string;
}

interface DisplayCatalogOption extends SelectedCatalogOption {
  description?: string;
}

interface MergedCatalogOption extends DisplayCatalogOption {
  description: string;
}

const getCustomCatalogIds = (ids: string[]) =>
  Array.from(new Set(ids.filter((id) => id !== DEFAULT_SUB_CATALOG_VALUE)));

export const filterSubCatalogOptions = <T extends DisplayCatalogOption>(
  options: T[],
  query: string
): T[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (!normalizedQuery) return options;

  return options.filter((option) =>
    `${option.label} ${option.description ?? option.value}`
      .toLocaleLowerCase()
      .includes(normalizedQuery)
  );
};

export const getSubCatalogSelectionSummary = (
  options: DisplayCatalogOption[],
  selectedIds: string[]
): string => {
  const customIds = getCustomCatalogIds(selectedIds);

  if (customIds.length === 0) {
    return (
      options.find(({ value }) => value === DEFAULT_SUB_CATALOG_VALUE)?.label ??
      'Default catalog'
    );
  }

  if (customIds.length > 2) {
    return `${customIds.length} catalogs selected`;
  }

  const optionsById = new Map(options.map((option) => [option.value, option]));

  return customIds.map((id) => optionsById.get(id)?.label ?? id).join(', ');
};

export const mergeSubCatalogOptions = (
  fetchedCatalogs: CatalogOptionSource[],
  selectedCatalogs: SelectedCatalogOption[]
): MergedCatalogOption[] => {
  const selectedById = new Map(
    selectedCatalogs.map((option) => [option.value, option])
  );
  const merged = fetchedCatalogs.map((catalog) => ({
    value: catalog.id,
    label: catalog.name || selectedById.get(catalog.id)?.label || catalog.id,
    description: catalog.id,
  }));
  const fetchedIds = new Set(fetchedCatalogs.map((catalog) => catalog.id));

  selectedCatalogs.forEach((option) => {
    if (!fetchedIds.has(option.value)) {
      merged.push({
        value: option.value,
        label: option.label || option.value,
        description: option.value,
      });
    }
  });

  return merged;
};

export const normalizeSubCatalogSelection = (
  currentIds: string[],
  nextIds: string[]
): string[] => {
  const currentCustomIds = getCustomCatalogIds(currentIds);
  const nextCustomIds = getCustomCatalogIds(nextIds);

  if (!nextIds.includes(DEFAULT_SUB_CATALOG_VALUE)) {
    return nextCustomIds;
  }

  return currentCustomIds.length > 0 ? [] : nextCustomIds;
};

export const getSubCatalogChanges = (
  currentIds: string[],
  selectedIds: string[]
): { createIds: string[]; removeIds: string[] } => {
  const currentCustomIds = getCustomCatalogIds(currentIds);
  const selectedCustomIds = getCustomCatalogIds(selectedIds);
  const currentIdSet = new Set(currentCustomIds);
  const selectedIdSet = new Set(selectedCustomIds);

  return {
    createIds: selectedCustomIds.filter((id) => !currentIdSet.has(id)),
    removeIds: currentCustomIds.filter((id) => !selectedIdSet.has(id)),
  };
};
